import { Pinkx, Positioning, Spacing } from "@bonusx-global/bx-design-system";
import type { ProfileSectionItem } from "@bonusx-global/bx-design-system/dist/types/components/profile-sections/interfaces/profile-sections.interface";
import {
  BriefcaseOutlined,
  BriefcaseSolid,
  BuildingTreeOutlined,
  BuildingTreeSolid,
  ChatQuestionSolid,
  PapersTextOutlined,
  PapersTextSolid,
  UserAltOutlined,
  UserAltSolid,
  UsersGroupAltOutlined,
  UsersGroupAltSolid,
  WalletOutlined,
  WalletSolid,
} from "@bonusx-global/bx-icons";
import { type SxProps } from "@mui/material";
import Box from "@mui/material/Box";
import type { NavigateFunction } from "react-router-dom";
import type { ProfileSectionsById } from "../../../../api/features/profile/use-profile.query";
import {
  PROFILE_ACTIVITY_DATA_VIEW_PATH,
  PROFILE_HOUSEHOLD_DATA_VIEW_PATH,
  PROFILE_INCOME_DATA_VIEW_PATH,
  PROFILE_ISEE_DATA_VIEW_PATH,
  PROFILE_PERSONAL_DATA_VIEW_PATH,
  PROFILE_REAL_ESTATE_DATA_VIEW_PATH,
} from "../../../../constants/env";
import { ProfileSections } from "../../constants/profile-sections.enum";

const questionBadgeStyles: SxProps = {
  position: Positioning.absolute,
  top: Spacing[0],
  right: `-${Spacing["650"]}`,
  width: Spacing["1250"],
  height: Spacing["1250"],
  color: Pinkx[300],
};

const TO_COMPLETE_TEXT = "Da completare";
const HOUSEHOLD_TEXT = "Nucleo familiare";
const ACTIVITY_TEXT = "Attività";
const PERSONAL_DATA_TEXT = "Dati personali";
const UNKNOWN_ISEE_TEXT = "ISEE non presente";
const ISEE_TEXT = "ISEE";
const LIVING_SITUATION_TEXT = "Casa";
const LIVING_SITUATION_AND_OTHERS_TEXT = "Casa e altri immobili";
const INCOME_TEXT = "Redditi";

function getSectionSubtitle(isComplete: boolean, hasQuestionMark?: boolean): string | undefined {
  if (isComplete) {
    return undefined;
  }

  if (hasQuestionMark) {
    return undefined;
  }

  return TO_COMPLETE_TEXT;
}

interface BuildSectionsFromBackendParams {
  sectionsById: ProfileSectionsById;
  navigate: NavigateFunction;
  isIncomeAvailable: boolean;
  isRealEstatesAvailable: boolean;
}

export function buildSectionsFromBackend(params: BuildSectionsFromBackendParams): ProfileSectionItem[] {
  const { sectionsById, navigate, isIncomeAvailable, isRealEstatesAvailable } = params;
  const personalDataSection = sectionsById[ProfileSections.PERSONAL_DATA];
  const activitySection = sectionsById[ProfileSections.ACTIVITY];
  const householdSection = sectionsById[ProfileSections.HOUSEHOLD];
  const livingSituationSection = sectionsById[ProfileSections.LIVING_SITUATION];
  const iseeSection = sectionsById[ProfileSections.ISEE];
  const incomeSection = sectionsById[ProfileSections.INCOME];

  const hasUnknownIsee = iseeSection?.hasQuestionMark === true;
  const iseeTitle = hasUnknownIsee ? UNKNOWN_ISEE_TEXT : ISEE_TEXT;

  const isPersonalDataComplete = personalDataSection?.isComplete ?? false;
  const isActivityComplete = activitySection?.isComplete ?? false;
  const isHouseholdComplete = householdSection?.isComplete ?? false;
  const isLivingSituationComplete = livingSituationSection?.isComplete ?? false;
  const isIseeComplete = iseeSection?.isComplete ?? false;
  const isIncomeComplete = incomeSection?.isComplete ?? false;

  let livingSituationTitle = LIVING_SITUATION_TEXT;
  if (isRealEstatesAvailable) {
    livingSituationTitle = LIVING_SITUATION_AND_OTHERS_TEXT;
  }

  const profileSections: ProfileSectionItem[] = [
    {
      id: ProfileSections.PERSONAL_DATA,
      icon: <UserAltOutlined />,
      iconCompleted: <UserAltSolid />,
      title: PERSONAL_DATA_TEXT,
      subtitle: getSectionSubtitle(isPersonalDataComplete),
      isComplete: isPersonalDataComplete,
      onClick: function handleDatiPersonaliClick() {
        navigate(PROFILE_PERSONAL_DATA_VIEW_PATH);
      },
    },
    {
      id: ProfileSections.ACTIVITY,
      icon: <BriefcaseOutlined />,
      iconCompleted: <BriefcaseSolid />,
      title: ACTIVITY_TEXT,
      subtitle: getSectionSubtitle(isActivityComplete),
      isComplete: isActivityComplete,
      onClick: function handleAttivitaClick() {
        navigate(PROFILE_ACTIVITY_DATA_VIEW_PATH);
      },
    },
    {
      id: ProfileSections.HOUSEHOLD,
      icon: <UsersGroupAltOutlined />,
      iconCompleted: <UsersGroupAltSolid />,
      title: HOUSEHOLD_TEXT,
      subtitle: getSectionSubtitle(isHouseholdComplete),
      isComplete: isHouseholdComplete,
      onClick: function handleNucleoFamiliareClick() {
        navigate(PROFILE_HOUSEHOLD_DATA_VIEW_PATH);
      },
    },
    {
      id: ProfileSections.LIVING_SITUATION,
      icon: <BuildingTreeOutlined />,
      iconCompleted: <BuildingTreeSolid />,
      title: livingSituationTitle,
      subtitle: getSectionSubtitle(isLivingSituationComplete),
      isComplete: isLivingSituationComplete,
      onClick: function handleLivingSituationClick() {
        navigate(PROFILE_REAL_ESTATE_DATA_VIEW_PATH);
      },
    },
    {
      id: ProfileSections.ISEE,
      icon: (
        <Box>
          <PapersTextOutlined />
          {hasUnknownIsee && <ChatQuestionSolid sx={questionBadgeStyles} />}
        </Box>
      ),
      iconCompleted: <PapersTextSolid />,
      title: iseeTitle,
      subtitle: getSectionSubtitle(isIseeComplete, iseeSection?.hasQuestionMark),
      isComplete: isIseeComplete,
      onClick: function handleISEEClick() {
        navigate(PROFILE_ISEE_DATA_VIEW_PATH);
      },
    },
  ];

  if (isIncomeAvailable) {
    const incomeSectionItem: ProfileSectionItem = {
      id: ProfileSections.INCOME,
      icon: <WalletOutlined />,
      iconCompleted: <WalletSolid />,
      title: INCOME_TEXT,
      subtitle: getSectionSubtitle(isIncomeComplete),
      isComplete: isIncomeComplete,
      onClick: function handleIncomeClick() {
        navigate(PROFILE_INCOME_DATA_VIEW_PATH);
      },
    };

    profileSections.push(incomeSectionItem);
  }

  return profileSections;
}
